import { useTranslation } from "react-i18next";
import { NavLink } from "react-router-dom";
import { useState, useEffect } from "react";
import { ChevronDown, Menu, X } from "lucide-react";
import { useLocation } from "react-router-dom";

const products = [
  { key: "communications", external: false },
  { key: "security", external: false },
  { key: "aiPeopleCounting", external: false },
];

const solutions = [
  { key: "skydial", external: false },
  { key: "voiceapi", external: false },
  { key: "aicounting", external: false },
  { key: "vms", external: false },
  { key: "soc", external: false },
  { key: "vapt", external: false },
  { key: "firstprotect", external: false },
  { key: "networking", external: false },
];

const Navbar = () => {
  const { t, i18n } = useTranslation();
  const [openLang, setOpenLang] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [openProducts, setOpenProducts] = useState(false);
  const [openSolutions, setOpenSolutions] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  const location = useLocation();
  const isHomePage = location.pathname === "/";

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (!document.contains(e.target)) return;
      if (!e.target.closest(".nav-dropdown")) {
        setOpenProducts(false);
        setOpenSolutions(false);
      }
    };
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  const changeLang = (lang) => {
    i18n.changeLanguage(lang);
    setOpenLang(false);
  };

  return (
    <header
      className={`fixed top-0 left-0 w-full z-20 py-2 md:px-20 sm:px-0 flex justify-between items-center border-b border-gray-200 ${
        isHomePage
          ? isScrolled
            ? "bg-white text-black shadow-sm"
            : "bg-transparent text-black"
          : "bg-white text-black"
      }`}
    >
      {/* Logo */}
      <NavLink to="/" className="flex items-center ml-16 md:ml-0 sm:ml-8">
        <img
          src="/skyglobal-removebg-preview.svg"
          alt="Skyglobal Logo"
          className="h-16 md:h-12"
        />
      </NavLink>

      {/* Desktop Nav */}
      <nav className="flex space-x-8 font-medium sm:hidden">
        <NavLink to="/" className="hover:text-bgYellow no-underline">
          {t("navbar.home")}
        </NavLink>

        {/* Products Dropdown */}
        <div
          className="relative nav-dropdown"
          onClick={(e) => {
            e.stopPropagation();
            if (location.pathname.startsWith("/products")) {
              setOpenProducts(!openProducts);
            } else {
              setOpenProducts(true);
            }
            setOpenSolutions(false);
          }}
        >
          <NavLink
            to="/products"
            className="flex items-center gap-1 hover:text-bgYellow no-underline"
          >
            {t("navbar.products")}
            <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${openProducts ? "rotate-180" : ""}`} />
          </NavLink>
          {openProducts && (
            <div className="absolute left-0 mt-2 w-56 bg-white border border-gray-200 rounded-lg shadow-lg py-2 z-10">
              {products.map((product) => (
                <NavLink
                  key={product.key}
                  to={`/products/${product.key}`}
                  className="block px-4 py-2 text-black hover:text-bgYellow hover:bg-gray-50 transition-colors no-underline"
                >
                  {t(`products.${product.key}.title`)}
                </NavLink>
              ))}
            </div>
          )}
        </div>

        {/* Solutions Dropdown */}
        <div
          className="relative nav-dropdown"
          onClick={(e) => {
            e.stopPropagation();
            if (location.pathname.startsWith("/solutions")) {
              setOpenSolutions(!openSolutions);
            } else {
              setOpenSolutions(true);
            }
            setOpenProducts(false);
          }}
        >
          <NavLink
            to="/solutions"
            className="flex items-center gap-1 hover:text-bgYellow no-underline"
          >
            {t("navbar.solutions")}
            <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${openSolutions ? "rotate-180" : ""}`} />
          </NavLink>
          {openSolutions && (
            <div className="absolute left-0 mt-2 w-56 bg-white border border-gray-200 rounded-lg shadow-lg py-2 z-10">
              {solutions.map((solution) => (
                solution.external ? (
                  <a
                    key={solution.key}
                    href={solution.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block px-4 py-2 text-black hover:text-bgYellow hover:bg-gray-50 transition-colors no-underline"
                  >
                    {t(`solutions.${solution.key}.title`)}
                  </a>
                ) : (
                  <NavLink
                    key={solution.key}
                    to={`/solutions/${solution.key}`}
                    className="block px-4 py-2 text-black hover:text-bgYellow hover:bg-gray-50 transition-colors no-underline"
                  >
                    {t(`solutions.${solution.key}.title`)}
                  </NavLink>
                )
              ))}
            </div>
          )}
        </div>

        <NavLink to="/about" className="hover:text-bgYellow no-underline">
          {t("navbar.about")}
        </NavLink>
        <NavLink to="/contact" className="hover:text-bgYellow no-underline">
          {t("navbar.contact")}
        </NavLink>
      </nav>

      {/* Language dropdown */}
      <div className="relative mr-24 sm:hidden">
        <button
          onClick={() => setOpenLang(!openLang)}
          className="flex items-center gap-1 border border-textBlue px-3 py-1 rounded text-sm hover:bg-textBlue hover:text-white transition"
        >
          {i18n.language.toUpperCase()}
          <ChevronDown className="w-4 h-4" />
        </button>
        {openLang && (
          <div className="absolute right-0 mt-2 w-24 bg-white border border-gray-200 rounded shadow-md text-sm z-10">
            <button
              onClick={() => changeLang("en")}
              className="block w-full text-left px-4 py-2 text-black hover:bg-bgYellow"
            >
              English
            </button>
            <button
              onClick={() => changeLang("id")}
              className="block w-full text-left px-4 py-2 text-black hover:bg-bgYellow"
            >
              Bahasa
            </button>
          </div>
        )}
      </div>

      {/* Hamburger Button (Mobile) */}
      <div className="hidden md:block sm:mr-8">
        <button onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}>
          {isMobileMenuOpen ? (
            <X className="w-6 h-6" />
          ) : (
            <Menu className="w-6 h-6" />
          )}
        </button>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="absolute top-full left-0 w-full bg-white shadow-md border-t border-gray-200 flex flex-col pl-8 py-6 gap-4 text-lg no-underline">
          <NavLink
            to="/"
            className={"hover:text-bgYellow no-underline text-black"}
            onClick={() => setIsMobileMenuOpen(false)}
          >
            {t("navbar.home")}
          </NavLink>

          {/* Mobile Products */}
          <div className="flex flex-col">
            <NavLink
              to="/products"
              className="hover:text-bgYellow no-underline text-black font-medium mb-2"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              {t("navbar.products")}
            </NavLink>
            <div className="flex flex-col pl-4 gap-2">
              {products.map((product) => (
                <NavLink
                  key={product.key}
                  to={`/products/${product.key}`}
                  className="text-base hover:text-bgYellow text-black no-underline"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  {t(`products.${product.key}.title`)}
                </NavLink>
              ))}
            </div>
          </div>

          {/* Mobile Solutions */}
          <div className="flex flex-col">
            <NavLink
              to="/solutions"
              className="hover:text-bgYellow no-underline text-black font-medium mb-2"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              {t("navbar.solutions")}
            </NavLink>
            <div className="flex flex-col pl-4 gap-2">
              {solutions.map((solution) => (
                solution.external ? (
                  <a
                    key={solution.key}
                    href={solution.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-base hover:text-bgYellow text-black no-underline"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    {t(`solutions.${solution.key}.title`)}
                  </a>
                ) : (
                  <NavLink
                    key={solution.key}
                    to={`/solutions/${solution.key}`}
                    className="text-base hover:text-bgYellow text-black no-underline"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    {t(`solutions.${solution.key}.title`)}
                  </NavLink>
                )
              ))}
            </div>
          </div>

          <NavLink
            to="/about"
            className={"hover:text-bgYellow no-underline text-black"}
            onClick={() => setIsMobileMenuOpen(false)}
          >
            {t("navbar.about")}
          </NavLink>
          <NavLink
            to="/contact"
            className={"hover:text-bgYellow no-underline text-black"}
            onClick={() => setIsMobileMenuOpen(false)}
          >
            {t("navbar.contact")}
          </NavLink>

          {/* Language Dropdown */}
          <div className="mt-2 border-t pt-4 w-[150px]">
            <select
              id="lang-select"
              value={i18n.language}
              onChange={(e) => changeLang(e.target.value)}
              className="w-full border border-gray-300 rounded py-1 text-lg"
            >
              <option value="en" className="text-sm px-4">
                English
              </option>
              <option value="id" className="text-sm">
                Bahasa
              </option>
            </select>
          </div>
        </div>
      )}
    </header>
  );
};

export default Navbar;
